import { useState, useEffect, useContext } from 'react';
import { Table, Button, Spin, Pagination, message, Drawer, Form, Input, DatePicker, Select, Modal, Checkbox } from 'antd';
import axios from 'axios';
import { LoginContext } from '../../../../context/loginContext';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { FilterOutlined } from '@ant-design/icons';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('America/Argentina/Buenos_Aires');

const { RangePicker } = DatePicker;
const { Option } = Select;

const CListaPagoAdministrador = () => {
    const [pagos, setPagos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [filtros, setFiltros] = useState({});
    const [form] = Form.useForm();
    const pageSize = 10;
    const { user, selectedEmpresa } = useContext(LoginContext);
    const [paymentTypes, setPaymentTypes] = useState([]);
    const [paymentTypesLoaded, setPaymentTypesLoaded] = useState(false);

    useEffect(() => {
        fetchPaymentTypes();
    }, []);

    useEffect(() => {
        if (paymentTypesLoaded) {
            fetchPagos();
        }
    }, [currentPage, filtros, paymentTypesLoaded]);


    const fetchPaymentTypes = async () => {
        try {
            setLoading(true);
            const url = `${process.env.REACT_APP_API_URL}/tipo-pago`;
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            const response = await axios.get(url, config);
            setPaymentTypes(response.data);
            setPaymentTypesLoaded(true);
        } catch (error) {
            console.error('Error fetching payment types:', error);
            message.error('Error al cargar los tipos de pago');
        } finally {
            setLoading(false);
        }
    };


    const fetchPagos = async () => {
        setLoading(true);
        try {
            // Construir la URL con los filtros
            let url = `${process.env.REACT_APP_API_URL}/pago/${selectedEmpresa.emp_id}?page=${currentPage}&limit=${pageSize}`;

            // Añadir filtros a la URL si existen
            if (filtros.pag_tipopago) {
                url += `&pag_tipopago=${filtros.pag_tipopago}`;
            }
            if (filtros.pag_comprobante) {
                url += `&pag_comprobante=${filtros.pag_comprobante}`;
            }
            if (filtros.pag_fechaorigen) {
                url += `&pag_fechaorigen=${filtros.pag_fechaorigen}`;
            }
            if (filtros.pag_cuenta) {
                url += `&pag_cuenta=${filtros.pag_cuenta}`;
            }

            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            const response = await axios.get(url, config);

            const { pagos, pagination } = response.data;

            // Mapear datos para que coincidan con la tabla
            const formattedPagos = pagos.map(pago => {
                const tipoPago = paymentTypes.find(type => Number(type.tpa_id) === Number(pago.pag_tipopago));
            
                return {
                    key: pago.pag_id || 'N/A',
                    fecha: pago.pag_fechaorigen 
                        ? dayjs.utc(pago.pag_fechaorigen).tz('America/Argentina/Buenos_Aires', true).format('DD/MM/YYYY') 
                        : 'Fecha no disponible',
                    tipo_pago: tipoPago?.tpa_nombre || 'Desconocido',
                    comprobante: pago.pag_comprobante?.toString() || 'Sin comprobante',
                    importe: pago.pag_importe 
                        ? `$${parseFloat(pago.pag_importe).toFixed(2)}` 
                        : '$0.00',
                    cuenta: pago.cue_nombre?.toString() || 'Sin descripción',
                    acreditado: pago.pag_acreditado,
                    pag_ingegr: pago.pag_ingegr
                };
            });
                       

            setPagos(formattedPagos);
            setTotalItems(pagination.totalItems);
        } catch (error) {
            if (error.response && error.response.status === 404) {
                const errorMessage = error.response.data.message || 'No se encontraron pagos para esta empresa';
                message.error(errorMessage);
                setPagos([]);
                setTotalItems(0);
            } else {
                message.error('Error al cargar los pagos');
            }
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const showDrawer = () => {
        setDrawerVisible(true);
    };

    const onClose = () => {
        setDrawerVisible(false);
    };

    const handleApplyFilters = (values) => {
        const nuevosFiltros = {};

        if (values.pag_tipopago) nuevosFiltros.pag_tipopago = values.pag_tipopago;
        if (values.pag_comprobante) nuevosFiltros.pag_comprobante = values.pag_comprobante;
        if (values.pag_cuenta) nuevosFiltros.pag_cuenta = values.pag_cuenta;

        // Procesar fechas
        if (values.fecha_origen) {
            nuevosFiltros.pag_fechaorigen = values.fecha_origen.format('YYYY-MM-DD');
        }

        setFiltros(nuevosFiltros);
        setCurrentPage(1); // Resetear a la primera página con nuevos filtros
        onClose();
    };

    const handleResetFilters = () => {
        form.resetFields();
        setFiltros({});
        setCurrentPage(1);
        onClose();
    };

    const handleAcreditadoChange = (checked, record) => {
        Modal.confirm({
            title: "Confirmar cambio de estado",
            content: "¿Desea cambiar el estado del pago?",
            okText: "Sí",
            cancelText: "No",
            onOk: async () => {
                try {
                    const url = `${process.env.REACT_APP_API_URL}/pago/acreditado/${record.key}`;
                    const config = { headers: { Authorization: `Bearer ${user.token}` } };
                    await axios.put(url, { pag_acreditado: checked }, config);
                    message.success("Estado del pago actualizado correctamente");
                    fetchPagos();
                } catch (error) {
                    console.error("Error al actualizar el estado del pago:", error);
                    message.error("Error al actualizar el estado del pago");
                }
            }
        });
    };

    const columns = [
        { title: 'Fecha', dataIndex: 'fecha', key: 'fecha' },
        { title: 'Cuenta', dataIndex: 'cuenta', key: 'cuenta' },
        { title: 'Tipo de Pago', dataIndex: 'tipo_pago', key: 'tipo_pago' },
        { title: 'Comprobante', dataIndex: 'comprobante', key: 'comprobante' },
        { title: 'Importe', dataIndex: 'importe', key: 'importe' },
        { 
            title: 'Movimiento', 
            dataIndex: 'pag_ingegr', 
            key: 'pag_ingegr',
            render: (value) => (value === 1 ? 'EGRESO' : 'INGRESO')
        },
        { 
            title: 'Acreditado', 
            dataIndex: 'acreditado', 
            key: 'acreditado', 
            render: (text, record) => (
                <Checkbox 
                    checked={record.acreditado}
                    onChange={(e) => handleAcreditadoChange(e.target.checked, record)}
                />
            )
        }
    ];
    

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h1>Pagos</h1>
                <Button
                    type="primary"
                    onClick={showDrawer}
                    icon={<FilterOutlined />}
                >
                    Filtros
                </Button>
            </div>

            {loading ? <Spin size="large" style={{ display: 'block', margin: '20px auto' }} /> : (
                <>
                    <Table
                        dataSource={pagos}
                        columns={columns}
                        rowKey="key"
                        pagination={false}
                    />
                    <Pagination
                        current={currentPage}
                        total={totalItems}
                        pageSize={pageSize}
                        onChange={handlePageChange}
                        style={{ marginTop: 16, textAlign: 'center' }}
                    />
                </>
            )}

            <Drawer
                title="Filtros de Pagos"
                placement="right"
                width={400}
                onClose={onClose}
                open={drawerVisible}
                footer={
                    <div style={{ textAlign: 'right' }}>
                        <Button onClick={handleResetFilters} style={{ marginRight: 8 }}>
                            Limpiar filtros
                        </Button>
                        <Button onClick={() => form.submit()} type="primary">
                            Aplicar filtros
                        </Button>
                    </div>
                }
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleApplyFilters}
                >
                    <Form.Item
                        name="pag_tipopago"
                        label="Tipo de Pago"
                    >
                        <Select placeholder="Seleccione tipo de pago" allowClear>
                            {paymentTypes.map(type => (
                                <Option key={type.tpa_id} value={type.tpa_id}>
                                    {type.tpa_nombre}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="pag_comprobante"
                        label="Número de Comprobante"
                    >
                        <Input placeholder="Ingrese número de comprobante" />
                    </Form.Item>

                    <Form.Item
                        name="fecha_origen"
                        label="Fecha de Origen"
                    >
                        <DatePicker 
                            style={{ width: '100%' }}
                            format="DD/MM/YYYY"
                        />
                    </Form.Item>

                    <Form.Item
                        name="pag_cuenta"
                        label="Cuenta"
                    >
                        <Input placeholder="Ingrese CUIT/CUIL" />
                    </Form.Item>
                </Form>
            </Drawer>
        </div>
    );
};

export default CListaPagoAdministrador;